/**
 * Shared cart helpers. Loaded globally so cart-items.js, assortment-drawer.js and
 * product-card-volume.js can all settle a rebuilt bundle, and read a cart error, without
 * duplicating logic.
 */

/**
 * Reads the human-readable reason out of a cart API error body. Shopify doesn't agree with itself
 * on which field carries it. The same availability refusal arrives as a bare string under errors
 * from one route and as description and message from another, and a validation failure arrives as
 * a list under each field it rejected. Any of the three can also be present but empty, which
 * choosing by type alone would take, so the first field actually holding something wins instead.
 * @param {object} data - Parsed response body.
 * @param {number|string} status - Response status, used only if the body explains nothing.
 * @returns {string} Reason to show the shopper.
 */
function parseCartApiError(data, status) {
  // An availability refusal puts the whole sentence in errors as a bare string, where a validation
  // one puts a list under each field it rejected. Read as an object either way, the string spreads
  // into characters, so it is taken as written.
  if (typeof data?.errors === 'string' && data.errors.trim() !== '') return data.errors;

  if (data?.errors && typeof data.errors === 'object') {
    const errors = Object.values(data.errors)
      .flat()
      .filter((entry) => typeof entry === 'string' && entry.trim() !== '');
    if (errors.length) return errors.join(', ');
  }

  const stated = [data?.description, data?.message]
    .find((field) => typeof field === 'string' && field.trim() !== '');

  return stated || theme.strings.cartError || String(status);
}

/**
 * Message for an add the cart couldn't fill in whole cases, counted in the unit the shopper buys
 * in. Shopify's own message counts units, which reads as a different number than the cases on
 * screen, and describes stock it has since given back.
 * @param {number} cases - Cases the line is left holding.
 * @returns {string} Reason to show the shopper.
 */
function casesAvailableMessage(cases) {
  const strings = window.theme?.volumeCardStrings || {};
  let template = strings.casesAvailableOther;

  if (cases < 1) {
    template = strings.casesAvailableNone;
  } else if (cases === 1) {
    template = strings.casesAvailableOne;
  }

  return template ? template.replace('[count]', cases) : theme.strings.cartError;
}

/**
 * Reads the cart fresh. Used where a reply can't be trusted to describe it: a refusal reports the
 * reason it refused, not the state it left behind.
 * @returns {Promise<object>} Cart.
 */
async function fetchCart() {
  const response = await fetch(`${theme.routes.cart}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store'
  });
  if (!response.ok) throw new Error(theme.strings.cartError);

  return response.json();
}

/**
 * Finds the line carrying a given set of properties. A line's key is built from its properties, so
 * a change that rewrites them rewrites the key too — including a change the cart only partly
 * honoured, which leaves the caller holding a key that no longer names anything.
 * @param {object} cart - Cart to search.
 * @param {object} properties - Properties the line is expected to carry.
 * @returns {?object} Matching line, or null.
 */
function findLineByProperties(cart, properties) {
  return cart.items?.find((item) => Object.entries(properties)
    .every(([name, value]) => item.properties?.[name] === value)) || null;
}

/**
 * Changes one cart line, returning the cart it leaves behind. Wraps the headers and the error
 * reading every caller shares, so a change reads as the body it sends. Callers that need to inspect
 * a refusal rather than raise it hold onto their own fetch.
 * @param {object} body - change.js parameters. Always state quantity: change.js reads an absent one
 * as 1 and empties the line down to a single unit.
 * @returns {Promise<object>} Cart, holding any sections asked for.
 */
async function changeCartLine(body) {
  const response = await fetch(theme.routes.cartChange, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(body)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(parseCartApiError(data, response.status));

  return data;
}

/**
 * Brings a line down to a whole number of cases, giving back any part of a case the cart took
 * while covering what stock it could. These products are sold by the case, so a line is never left
 * holding part of one, which neither the shopper nor the tier pricing has a way to express.
 * @param {string} key - Key of the line to settle.
 * @param {number} caseSize - Units per case.
 * @param {object} [cart] - Cart, where a trustworthy copy is already to hand.
 * @param {Function} [buildProperties] - Given the settled quantity, returns the properties to write
 * alongside the correction. For a line whose own set records the case count, which would otherwise
 * go on describing the quantity that was asked for.
 * @param {Array} [sections] - Sections to re-render with a correction.
 * @returns {Promise<object>} Cases held, whether any were given back, and the cart holding them.
 */
async function settleWholeCases(key, caseSize, cart, buildProperties, sections) {
  const units = caseSize > 1 ? caseSize : 1;
  const known = cart || await fetchCart();
  const line = known.items?.find((item) => item.key === key);

  // A line that is no longer there holds nothing, which is already a whole number of cases.
  if (!line) return { cases: 0, trimmed: false, cart: known };

  const whole = Math.floor(line.quantity / units) * units;
  if (whole === line.quantity) return { cases: whole / units, trimmed: false, cart: known };

  const properties = buildProperties ? buildProperties(whole) : null;

  const settled = await changeCartLine({
    id: key,
    quantity: whole,
    ...(properties ? { properties } : {}),
    ...(sections ? { sections, sections_url: window.location.pathname } : {})
  });

  return { cases: whole / units, trimmed: true, cart: settled };
}

/**
 * Confirms a rebuilt bundle's line holds exactly one bundle, and puts it back to one if it doesn't.
 * The rewrites all state a quantity of 1 themselves, so this normally costs nothing; it catches the
 * line an add merged into when the lookup that would have found it failed, because a count above one
 * silently multiplies the bundle behind the pricing the properties describe.
 * @param {object} cart - Cart returned by the rewrite.
 * @param {object} properties - Properties written, used to identify the line.
 * @param {Array} sections - Sections to re-render.
 * @returns {Promise<object>} Cart to render, the rewrite's own unless the count needed settling.
 */
async function settleComboQuantity(cart, properties, sections) {
  // Identified by the properties just written rather than by position, so a shifted cart can
  // never have the count settled on a different line.
  const line = findLineByProperties(cart, properties);
  if (!line) throw new Error(theme.strings.cartError);
  if (line.quantity === 1) return cart;

  return changeCartLine({
    id: line.key,
    quantity: 1,
    sections,
    sections_url: window.location.pathname
  });
}
